import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Clock, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DateTimePicker, Field, FormSkeleton, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { scheduleAccess, selectableDoctors } from './schedule-access';
import { safeReturnTo } from './BookAppointmentPage';
import { BulkMovePanel, type AffectedBooking } from './BulkMove';

interface DoctorOption {
  id: string;
  name: string;
  departmentId: string | null;
}

export function ScheduleLeavePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can, user } = useAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), '/schedules');

  const access = scheduleAccess({ all: can('schedule.manage'), own: can('schedule.manage.own') }, user?.id ?? null);
  const self = user ? { id: user.id, name: user.displayName } : null;
  const defaultDoctorId = access.onlyOwn ? (self?.id ?? '') : '';

  const bookable = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () => (await get<{ data: DoctorOption[] }>('/org/clinicians?kind=bookable&limit=200')).data,
  });
  const doctors = selectableDoctors(access, bookable.data ?? [], self);

  const [doctorId, setDoctorId] = useState(defaultDoctorId);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState<'leave' | 'conference' | 'surgery' | 'other'>('leave');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [affected, setAffected] = useState<AffectedBooking[] | null>(null);

  const impact = useQuery({
    queryKey: ['block-impact', doctorId, startsAt, endsAt],
    queryFn: () =>
      get<{ bookings: AffectedBooking[]; truncated: boolean }>(
        `/schedules/blocks/impact${qs({ doctorId, startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString() })}`,
      ),
    enabled: Boolean(doctorId && startsAt && endsAt && endsAt > startsAt),
  });

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['schedule-blocks'] });
    navigate(returnTo, { replace: true });
  };

  const save = useMutation({
    mutationFn: () =>
      post<{ affected?: { bookings: AffectedBooking[] } }>('/schedules/blocks', {
        doctorId,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        reason,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (result) => {
      toast.success(tr('opd', 'leave_recorded'), tr('opd', 'slots_in_this_window_are_no_longer_bookable'));
      if (result.affected?.bookings.length) {
        setAffected(result.affected.bookings);
        return;
      }
      finish();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(doctorId) && Boolean(startsAt) && Boolean(endsAt) && endsAt > startsAt && !affected;
  const dirty = !save.isSuccess && (doctorId !== defaultDoctorId || startsAt !== '' || endsAt !== '' || reason !== 'leave' || notes !== '');

  if (affected) {
    return (
      <FormScreen
        title={tr('opd', 'mark_leave')}
        description={tr('opd', 'blocks_out_a_window_without_touching_the_weekly')}
        icon={CalendarOff}
        backTo={returnTo}
      >
        <BulkMovePanel bookings={affected} doctorId={doctorId} doctors={doctors} onDone={finish} />
      </FormScreen>
    );
  }

  return (
    <FormScreen
      title={tr('opd', 'mark_leave')}
      description={tr('opd', 'blocks_out_a_window_without_touching_the_weekly')}
      icon={CalendarOff}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('opd', 'doctor')} icon={UserRound} columns={1}>
        {bookable.isLoading ? (
          <FormSkeleton />
        ) : (
          <Field label={tr('opd', 'doctor')} required>
            <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
              <option value="">{tr('opd', 'select_a_doctor')}</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'when')} icon={Clock}>
        <Field label={tr('opd', 'from')} required>
          <DateTimePicker value={startsAt} onChange={setStartsAt} required />
        </Field>
        <Field label={tr('opd', 'to')} required>
          <DateTimePicker value={endsAt} onChange={setEndsAt} min={startsAt || undefined} required />
        </Field>
        {(impact.data?.bookings.length ?? 0) > 0 && (
          <Alert variant="warning" className="sm:col-span-2">
            {impact.data?.bookings.length}
            {impact.data?.truncated ? '+' : ''} {tr('opd', 'booking_s_fall_inside_this_window_after_saving')}
          </Alert>
        )}
      </FormSection>

      <FormSection title={tr('opd', 'reason')}>
        <Field label={tr('opd', 'reason')}>
          <ChoiceButtons
            layout="chips"
            value={reason}
            onChange={setReason}
            options={[
              { value: 'leave', label: tr('opd', 'leave') },
              { value: 'conference', label: tr('opd', 'conference') },
              { value: 'surgery', label: tr('opd', 'surgery') },
              { value: 'other', label: tr('opd', 'other') },
            ]}
          />
        </Field>
        <Field label={tr('opd', 'notes')} className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('opd', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
